/**
 * paramateUndoManager.ts
 *
 * Turns Paramate configure cycles, input changes, and custom-data edits into
 * undo/redo history entries, plus the upload-token lifecycle that rides along
 * with them (a token introduced by an action that gets undone-past or cleared
 * must be released on the server; a token an undone action still owns must
 * survive so redo can bring it back).
 *
 * This sits on top of the plain History stack in undoHistory.ts — that
 * module only knows how to push/pop entries. This module knows what a
 * Paramate configure cycle, an input edit, or a custom-data change actually
 * mean in terms of when to snapshot, when to skip, and what to clean up.
 *
 * A plain module with module-level state, like undoHistory.ts and the rest of
 * this codebase's stores — there is only ever one live configurator/history
 * timeline at a time (the whole design already assumes that: one global
 * History stack, one `window.cfgr`, one set of `as-undo`/`as-redo` window
 * listeners), so a singleton here isn't a new constraint.
 *
 * as-configurator.ts calls bindConfigurator() once (constructor) so this
 * module can always read the CURRENT live Paramate instance — a callback,
 * not a stored reference, because a session reinit swaps in a fresh instance
 * (see docs/as-configurator.md#session-reinitialization) — then feeds this
 * module the lifecycle moments it already listens for (before-configure,
 * after-configure, configure error, input change, custom-data change,
 * project load/reset).
 */

import { devlog } from "../../../util/utils.ts";
import { currentProjectData } from "../../../data/projectManagement.ts";
import { internalData } from "../../../store/store.ts";
import * as History from "../../../features/history/undoHistory.ts";
import type { HistoryAction } from "../../../features/history/undoHistory.ts";
import { sendEv } from '../../../data/events.ts';
import { paramateInputRegistry } from '../../../store/paramInputRegistry.ts';

/**
 * Deep clone for JSON-like Paramate param values. We roll our own instead of
 * `structuredClone` because one target host lacks it. Handles primitives,
 * arrays, plain objects and Dates; anything exotic (functions, class instances)
 * is returned as-is — param values are server-serializable, so those don't occur.
 *
 * Used to detach history snapshots (and the values handed back to `configure()`
 * on undo/redo) from the live param objects. Without this, a trCAD write-back
 * that mutates an array-valued param in place (e.g. the workpiece model-matrix
 * Z-correction) would reach the stored undo snapshot by reference and cause
 * redo drift. See docs/guiControls.md.
 */
export function cloneParamValue<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    const out = new Array(value.length);
    for (let i = 0; i < value.length; i++) out[i] = cloneParamValue(value[i]);
    return out as unknown as T;
  }
  if (value instanceof Date) return new Date(value.getTime()) as unknown as T;
  const out: Record<string, any> = {};
  for (const key in value as any) {
    if (Object.prototype.hasOwnProperty.call(value, key)) out[key] = cloneParamValue((value as any)[key]);
  }
  return out as T;
}

/**
 * Structural equality for param values, used by the undo/redo diff. Scalars
 * compare by `===`; arrays/objects compare by structure. Needed because cloned
 * snapshots (see cloneParamValue) never share a reference, so a reference test
 * would flag every array/object param as "changed" on every restore.
 */
export function paramValuesEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  const aArr = Array.isArray(a), bArr = Array.isArray(b);
  if (aArr !== bArr) return false;
  if (aArr) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!paramValuesEqual(a[i], b[i])) return false;
    return true;
  }
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }
  const aKeys = Object.keys(a), bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    if (!paramValuesEqual(a[k], b[k])) return false;
  }
  return true;
}

/** Strip the `main:` prefix so token-tracking keys are consistent regardless of how
 *  the caller specified the param name. configurator.params uses `main:foo`; trcad-param
 *  attributes may or may not include the prefix. */
export function normalizeTokenKey(k: string): string {
  return k.replace(/^main:/, '');
}

// ==============================================================================
// Module state
// ==============================================================================

/** Reads the live Paramate configurator instance. A callback (not a stored
 *  reference) because a session reinit swaps in a fresh instance. */
let _getConfigurator: () => any = () => null;

/** True once the host has finished its first bind to a configurator instance —
 *  gates the very first configure after bind from being recorded (there's
 *  nothing to undo back to). */
let _configuratorReady = false;
let _preConfigureSnapshot: { params: Record<string, any> } | null = null;
/** One-shot flag: the next configure/restore cycle should not produce a
 *  history entry (skipHistory callers, reinit, restore, project load). */
let _suppressNextEntry = false;
let _lastConfirmedParams: Record<string, any> = {};
let _lastCustomData: Record<string, any> = {};
let _customDataChangeTimer: ReturnType<typeof setTimeout> | null = null;
/** fileId (main: prefix stripped) → upload token. Keeps tokens alive across undo
 *  so redo can re-activate them; entries are removed only when history branches or clears. */
let _uploadedTokens = new Map<string, string>();
let _isUndoingConfigure = false;
let _pendingConfigureLabel: string | null = null;
let _pendingRestoreCompletion: (() => void) | null = null;

/** Point this module at the live configurator accessor. Call once — see the
 *  file header for why this is a singleton. */
export function bindConfigurator(getConfigurator: () => any): void {
  _getConfigurator = getConfigurator;
}

// ==============================================================================
// Baseline / readiness / suppression
// ==============================================================================

export function markReady(): void {
  _configuratorReady = true;
  // Starting a session runs a configure of its own, and it is usually still in
  // flight when this is called. Its beforeConfigure() has already taken a
  // snapshot, so without dropping it here afterConfigure() would record the
  // session coming up as the first undo step. There is nothing to undo back to
  // at that point, which is what the readiness gate is for.
  _preConfigureSnapshot = null;
}
export function markNotReady(): void { _configuratorReady = false; }

/** Skip recording the next configure/restore cycle. */
export function suppressNext(): void { _suppressNextEntry = true; }
export function clearSuppress(): void { _suppressNextEntry = false; }
export function isSuppressed(): boolean { return _suppressNextEntry; }

/** Re-snapshot the "ground truth" params/customData that future undo/redo
 *  diffs are taken against. Call whenever the live session's state was just
 *  replaced wholesale (bind, reinit, restore, clear) rather than by a
 *  recorded action. Returns the freshly-flattened params, for callers that
 *  need to hand the same snapshot on to something else (e.g. an
 *  as-restore-param-values dispatch) without recomputing it. */
export function captureBaseline(): Record<string, any> {
  _lastConfirmedParams = _flattenParams();
  _lastCustomData = { ...currentProjectData.appData };
  return _lastConfirmedParams;
}

// ==============================================================================
// Public undo/redo API
// ==============================================================================

/** Reverts the last action. For configure actions that introduced a file upload token,
 *  the token is deleted from the server BEFORE the restore configure runs — this is
 *  necessary because Paramate will re-use a session file if it is still present,
 *  preventing the configure from reverting to the "no file" state. */
export function undo(): void {
  if (History.inProgress()) {
    devlog('[CFGR-HISTORY] Undo skipped - history action in progress');
    return;
  }
  const action = History.undoAction();
  if (!action) return;

  devlog('[CFGR-HISTORY] Undoing action:', action.type, 'clearsRedo:', action.clearsRedoOnUndo);
  sendEv(window, 'as-undo-start', { detail: { action } });

  if (action.clearsRedoOnUndo) {
    // Redo is impossible once the token is deleted, so truncate the stack.
    // truncateRedo() returns everything AFTER new _head, which includes 'action'
    // if it was already undone via History.undoAction().
    const abandoned = History.truncateRedo();
    _releaseAbandonedTokens(abandoned);
    // Also release tokens for the action we just undid since redo is cleared
    if (action.type === 'configure') {
      _releaseAbandonedTokens([action]);
    }
  }

  _performRestore(action, 'undo');
}

/** Re-applies an undone action. If the action included a file upload, the token stored
 *  is still live and Paramate can reactivate it. */
export function redo(): void {
  if (History.inProgress()) {
    devlog('[CFGR-HISTORY] Redo skipped - history action in progress');
    return;
  }
  const action = History.redoAction();
  if (!action) return;

  devlog('[CFGR-HISTORY] Redoing action:', action.type);
  sendEv(window, 'as-redo-start', { detail: { action } });
  _performRestore(action, 'redo');
}

/** `as-undo` window-event handler: routes through a confirmation event first
 *  if undoing this entry would clear an otherwise-available redo. */
export function handleUndoRequest(): void {
  if (History.peekUndo()?.clearsRedoOnUndo && History.canRedo()) {
    sendEv(window, 'as-undo-requires-confirm');
    return;
  }
  undo();
}
export function handleUndoConfirmed(): void { undo(); }
export function handleRedoRequest(): void { redo(); }

export function handleTruncateRedoRequest(): void {
  const abandoned = History.truncateRedo();
  _releaseAbandonedTokens(abandoned);
}

/** @param skipRemoteDelete When true, skip the per-token deleteFile() cleanup
 *  (still clears local bookkeeping) — for callers abandoning the WHOLE old
 *  session for a fresh one (_reinitSession), where chasing individual file
 *  tokens on the way out is pointless (and, if it raced the session swap,
 *  used to be an extra 401 in the console). */
export function clearHistory(skipRemoteDelete = false): void {
  if (_customDataChangeTimer) {
    clearTimeout(_customDataChangeTimer);
    _customDataChangeTimer = null;
  }
  const abandoned = History.resetHistory();
  if (!skipRemoteDelete) _releaseAbandonedTokens(abandoned);
  _uploadedTokens.clear();
  captureBaseline();
  // Intentionally NOT clearing suppressNext here.
  // handleResetConfigurator suppresses before starting an async configurator.reset(),
  // and clearHistory() can be called multiple times before that cycle completes.
  // Callers that need suppress cleared do so explicitly (_handleProjectLoaded,
  // the bindConfigurator callback, and onConfigureError).
}

// ==============================================================================
// Configure-cycle hooks (called by as-configurator's paramate event handlers)
// ==============================================================================

/** Snapshot the current baseline before a configure request goes out, unless
 *  an undo/redo replay is already driving this cycle or the caller asked to
 *  skip history for it. */
export function beforeConfigure(): void {
  if (!History.inProgress() && !_suppressNextEntry) {
    _preConfigureSnapshot = { params: { ..._lastConfirmedParams } };
  }
}

/** Called once a configure cycle has fully resolved (params are ground truth
 *  again). Either resumes an in-progress undo/redo, skips recording (suppressed),
 *  or pushes a new history entry for a normal user-triggered configure. */
export function afterConfigure(): void {
  const paramsNow = _flattenParams();
  _lastConfirmedParams = paramsNow;
  _lastCustomData = { ...currentProjectData.appData };

  if (History.inProgress()) {
    if (_isUndoingConfigure) {
      _isUndoingConfigure = false;
      _reapplyStagedActions();
    }
    _pendingRestoreCompletion?.();
    _pendingRestoreCompletion = null;
  } else if (_suppressNextEntry) {
    devlog('[CFGR-HISTORY] afterConfigure: skipping history entry (suppressNextEntry=true)');
    _suppressNextEntry = false;
  } else if (_preConfigureSnapshot && _configuratorReady) {
    const label = _pendingConfigureLabel;
    _pendingConfigureLabel = null;
    const action: HistoryAction = {
      type:         'configure',
      paramsBefore: _preConfigureSnapshot.params,
      paramsAfter:  paramsNow,
    };
    if (_isActionDestructive(action)) {
      action.clearsRedoOnUndo = true;
    }
    _dropSentStagedInputEntries(action.paramsBefore, action.paramsAfter);
    const abandoned = History.pushAction(action, label || undefined);
    _releaseAbandonedTokens(abandoned);
  }
  _preConfigureSnapshot = null;
}

/** Called when a configure cycle failed instead of resolving — releases
 *  whatever afterConfigure() would otherwise have released. */
export function onConfigureError(): void {
  if (History.inProgress()) {
    _isUndoingConfigure = false;
    _pendingRestoreCompletion?.();
    _pendingRestoreCompletion = null;
  }
}

// ==============================================================================
// Input / custom-data recording
// ==============================================================================

/** Push an 'input' history entry for an input element that doesn't self-manage
 *  its own history (see HistoryTrackingMixin). Caller decides whether this
 *  should be called at all (el._historyManaged, trcad-param + auto-configure
 *  elements already get a 'configure' entry instead). */
export function recordInputChange(inputId: string, valueBefore: any, valueAfter: any, clearsRedoOnUndo = false): void {
  if (History.inProgress()) return;
  const action: HistoryAction = { type: 'input', inputId, valueBefore, valueAfter };
  if (clearsRedoOnUndo) action.clearsRedoOnUndo = true;
  History.pushAction(action);
}

/** `as-custom-data-changed` window-event handler. Debounced (setTimeout 0) so several
 *  synchronous customData writes in the same tick collapse into one history entry. */
export function onCustomDataChanged(): void {
  if (History.inProgress() || _suppressNextEntry) return;
  if (!_customDataChangeTimer) {
    const beforeSnapshot = { ..._lastCustomData };
    _customDataChangeTimer = setTimeout(() => {
      _customDataChangeTimer = null;
      if (_suppressNextEntry) return; // double-check after timeout
      const afterSnapshot = { ...currentProjectData.appData };

      // Deep equal check to avoid redundant entries
      if (JSON.stringify(beforeSnapshot) === JSON.stringify(afterSnapshot)) return;

      _lastCustomData = { ...afterSnapshot };
      devlog('[CFGR-HISTORY] Pushing custom-data change');
      History.pushAction({ type: 'custom-data', before: beforeSnapshot, after: afterSnapshot });
    }, 0);
  }
}

// ==============================================================================
// Upload-token lifecycle
// ==============================================================================

/** Record a token an upload just produced, so it can be released on abandon
 *  or kept alive across undo for redo to reactivate. */
export function trackUploadToken(fileId: string, token: string): void {
  _uploadedTokens.set(normalizeTokenKey(fileId), token);
}

/** Delete every tracked token for a param (its primary key plus the `<param>:<i>`
 *  siblings a multi-file upload adds) and forget them locally. */
export function releaseTokensForParam(fileParamName: string): void {
  const normKey = normalizeTokenKey(fileParamName);
  for (const [key, token] of [..._uploadedTokens.entries()]) {
    if (key !== normKey && !key.startsWith(`${normKey}:`)) continue;
    _safeDeleteFile(token, `param ${fileParamName}`);
    _uploadedTokens.delete(key);
  }
}

/** The next configure-driven history entry (if one is recorded) should carry
 *  this label — set right before an upload's setParamValue/configure call. */
export function setPendingLabel(label: string): void {
  _pendingConfigureLabel = label;
}

// ==============================================================================
// Internal: restore / replay
// ==============================================================================

/**
 * Drop the input entries of staged edits this configure has just sent.
 *
 * A trcad-param input without configure-on-change stages its value and
 * records an input entry for the staged edit. Once a configure sends that
 * param, undoing the configure restores the input by itself, so the input
 * entry would make one click two undos. Entries come off the head only, so
 * nothing older than the last confirmed configure is touched. An entry stays
 * when its param is still dirty: the configure did not carry it, and the
 * edit is still pending.
 */
function _dropSentStagedInputEntries(before: Record<string, any>, after: Record<string, any>): void {
  const params = _getConfigurator()?.params;
  const sent = new Set<string>();
  for (const key of Object.keys(after)) {
    if (paramValuesEqual(before[key], after[key])) continue;
    if (params?.[key]?.dirty) continue;
    sent.add(normalizeTokenKey(key));
  }
  if (!sent.size) return;
  for (;;) {
    const head = History.peekUndo();
    if (head?.type !== 'input') return;
    if (!_paramsOfInput(head.inputId).some(p => sent.has(normalizeTokenKey(p)))) return;
    devlog(`[CFGR-HISTORY] Dropping staged input entry ${head.inputId}: its configure is the entry`);
    History.discardHead();
  }
}

/** The params the registered input with this id is linked to. */
function _paramsOfInput(inputId: string): string[] {
  const out: string[] = [];
  for (const [paramName, entries] of paramateInputRegistry) {
    for (const entry of entries) {
      if (entry.element.deref()?.id === inputId) out.push(paramName);
    }
  }
  return out;
}

function _flattenParams(): Record<string, any> {
  const configurator = _getConfigurator();
  if (!configurator?.params) return {};
  const flat: Record<string, any> = {};
  for (const [key, data] of Object.entries(configurator.params)) {
    // Clone so history snapshots are detached from the live param objects —
    // an in-place trCAD write-back must not reach a stored undo snapshot.
    flat[key] = cloneParamValue((data as any)?.value ?? (data as any)?.default);
  }
  return flat;
}

async function _performRestore(action: HistoryAction, direction: 'undo' | 'redo'): Promise<void> {
  const configurator = _getConfigurator();
  if (action.type === 'configure') {
    const params  = direction === 'undo' ? action.paramsBefore : action.paramsAfter;
    const against = direction === 'undo' ? action.paramsAfter  : action.paramsBefore;
    const changed: Record<string, any> = {};

    for (const [k, v] of Object.entries(params)) {
      // Value comparison (not reference): cloned snapshots never share a ref,
      // so a `!==` test would flag every array/object param as changed.
      if (!paramValuesEqual(against[k], v)) changed[k] = v;
    }

    devlog(`[CFGR-HISTORY] Performing ${direction} restore. Changed params:`, changed);

    if (direction === 'undo') {
      _isUndoingConfigure = true;
    }

    // Restore customData if it was bundled with this configure (squashed)
    if (action.customDataBefore && action.customDataAfter) {
      const data = direction === 'undo' ? action.customDataBefore : action.customDataAfter;
      currentProjectData.appData = { ...data };
      _lastCustomData = { ...data };
    }

    // Inputs a batch folded into this configure.
    if (action.inputsBefore && action.inputsAfter) {
      const inputs = direction === 'undo' ? action.inputsBefore : action.inputsAfter;
      sendEv(window, 'as-restore-input-values', { detail: { inputs, reason: 'history' } });
      internalData.uiInputs = { ...internalData.uiInputs, ...inputs };
    }

    // Restore input visual state and pending indicators synchronously
    sendEv(window, 'as-restore-param-values', { detail: { params: changed } });

    const entries = Object.entries(changed);
    if (entries.length === 0) {
      devlog('[CFGR-HISTORY] No params changed in restore, skipping configure');
      if (_isUndoingConfigure) {
        _isUndoingConfigure = false;
        _reapplyStagedActions();
      }
      History.completeAction(action, direction);
      return;
    }

    // Snapshot pending dirty params not being restored so they can be re-staged
    // after the configure completes — they should survive undo/redo as pending.
    const pendingDirty: Record<string, any> = {};
    const restoreKeys = new Set(entries.map(([k]) => k));
    if (configurator?.params) {
      for (const [key, data] of Object.entries(configurator.params as any)) {
        if ((data as any)?.dirty && !restoreKeys.has(key)) {
          pendingDirty[key] = (data as any).value;
        }
      }
    }

    _pendingRestoreCompletion = () => {
      // Re-stage dirty params that existed before this undo/redo.
      for (const [key, val] of Object.entries(pendingDirty)) {
        configurator?.setParamValue(key, val, false);
      }
      History.completeAction(action, direction);
    };

    // Send only the restore params directly to avoid Paramate's dirty-param sweep.
    // Coerce undefined to "" — file params have no default so their "before" snapshot
    // is undefined, and Paramate ignores undefined values instead of clearing the param.
    const configChanges: Record<string, { value: any }> = {};
    for (const [k, v] of entries) {
      devlog(`[CFGR-HISTORY] Restoring ${k} = ${v ?? ''}`);
      // Clone the value handed to configure() so an in-place write-back during
      // this restore cannot mutate the stored snapshot (v points into it).
      configChanges[k] = { value: cloneParamValue(v ?? '') };
    }
    // See as-configurator's 'as-configure' listener for why this rejection must be
    // caught here too rather than left to become a raw unhandled-rejection.
    Promise.resolve(configurator.configure(configChanges)).catch((err: any) =>
      devlog('[CFGR-HISTORY] undo/redo configure failed (handled via paramate-configurator-error):', err));

  } else if (action.type === 'custom-data') {
    const data = direction === 'undo' ? action.before : action.after;
    currentProjectData.appData = { ...data };
    _lastCustomData = { ...data };
    History.completeAction(action, direction);
  } else if (action.type === 'input') {
    const value = direction === 'undo' ? action.valueBefore : action.valueAfter;
    sendEv(window, 'as-restore-input-values', { detail: { inputs: { [action.inputId]: value }, reason: 'history' } });
    internalData.uiInputs = { ...internalData.uiInputs, [action.inputId]: value };
    History.completeAction(action, direction);
  }
}

/**
 * Re-apply all input and custom-data actions that occur after the last
 * successful configure call up to the current history head.
 * This is used when undoing a configuration to restore 'pending' changes.
 */
function _reapplyStagedActions(): void {
  const stack = History.getStack();
  const head = History.getHead();
  if (head < 0) return;

  // Find the last configuration action before or at head
  let lastConfigIdx = -1;
  for (let i = head; i >= 0; i--) {
    if (stack[i].type === 'configure') {
      lastConfigIdx = i;
      break;
    }
  }

  // Re-apply every non-configure action from the ground truth onwards
  for (let i = lastConfigIdx + 1; i <= head; i++) {
    const action = stack[i];
    if (action.type === 'input') {
      sendEv(window, 'as-restore-input-values', {
        detail: { inputs: { [action.inputId]: action.valueAfter }, reason: 'history' }
      });
    } else if (action.type === 'custom-data') {
      currentProjectData.appData = { ...action.after };
      _lastCustomData = { ...action.after };
    }
  }
}

// ==============================================================================
// Internal: upload-token release
// ==============================================================================

/** Delete a tracked upload token, swallowing failures (already-gone token,
 *  session no longer valid, etc.) so this never surfaces as an unhandled
 *  rejection. */
function _safeDeleteFile(token: string, label: string): void {
  const configurator = _getConfigurator();
  Promise.resolve(configurator?.deleteFile?.(token, () => {
    devlog(`[CFGR-HISTORY] Deleted file token ${token} for ${label}`);
  })).catch((err: any) => {
    devlog(`[CFGR-HISTORY] Failed to delete file token ${token} for ${label}:`, err);
  });
}

/** All tokens tracked for a param: its primary key plus the `<param>:<i>`
 *  keys a multi-file upload adds. */
function _trackedTokensFor(normKey: string): Set<string> {
  const tokens = new Set<string>();
  for (const [key, token] of _uploadedTokens.entries()) {
    if (key === normKey || key.startsWith(`${normKey}:`)) tokens.add(token);
  }
  return tokens;
}

// A configure is destructive when it brings a tracked upload token into a
// param: undoing it deletes the file, so redo is impossible. Snapshots hold
// every param, so a token has to be new in `after`, not merely present. A
// later configure that leaves the token where it is can be undone and redone.
function _isActionDestructive(action: HistoryAction): boolean {
  if (action.type !== 'configure') return false;
  for (const [paramName, afterVal] of Object.entries(action.paramsAfter)) {
    if (!afterVal) continue;
    const tracked = _trackedTokensFor(normalizeTokenKey(paramName));
    if (!tracked.size) continue;
    // Scalar file params hold one token; file[] params a token array.
    const afterTokens = Array.isArray(afterVal) ? afterVal : [afterVal];
    const beforeVal = action.paramsBefore[paramName];
    const beforeTokens = new Set(Array.isArray(beforeVal) ? beforeVal : [beforeVal]);
    if (afterTokens.some(v => typeof v === 'string' && tracked.has(v) && !beforeTokens.has(v))) return true;
  }
  return false;
}

/**
 * Called when history entries are permanently abandoned (pushAction truncation or clearHistory).
 * NOT called on undo — tokens must survive undo so that redo can restore them.
 * Keys in paramsAfter use the configurator's `main:` prefix; token-tracking keys are
 * normalised (no prefix), so we strip before lookup. A token is only released if it's absent from both
 * the abandoned action's "before" state AND the param's current live value — clearHistory()
 * passes the entire stack here, which can include the entry for a configure that just
 * succeeded, so "introduced by an abandoned action" alone doesn't mean the token is stale.
 */
function _releaseAbandonedTokens(actions: HistoryAction[]): void {
  for (const action of actions) {
    if (action.type === 'configure') {
      for (const [paramName, afterVal] of Object.entries(action.paramsAfter)) {
        if (!afterVal) continue;
        const normKey = normalizeTokenKey(paramName);
        const beforeVal = action.paramsBefore[paramName];
        // Scalar file params hold one token; file[] params a token array.
        // Release every tracked token this abandoned action introduced
        // (present after, absent before).
        const afterTokens = new Set(Array.isArray(afterVal) ? afterVal : [afterVal]);
        const beforeTokens = new Set(Array.isArray(beforeVal) ? beforeVal : [beforeVal]);
        // The param's current live value (post the latest successful configure) —
        // never release a token still in active use. clearHistory() abandons the
        // WHOLE stack at once, including the entry for a configure that just
        // succeeded (e.g. a destructive file replace), so "introduced by an
        // abandoned action" alone isn't enough to mean "safe to delete".
        const liveVal = _lastConfirmedParams[paramName];
        const liveTokens = new Set(Array.isArray(liveVal) ? liveVal : [liveVal]);
        for (const [key, token] of [..._uploadedTokens.entries()]) {
          if (key !== normKey && !key.startsWith(`${normKey}:`)) continue;
          if (afterTokens.has(token) && !beforeTokens.has(token) && !liveTokens.has(token)) {
            _safeDeleteFile(token, `param ${paramName}`);
            _uploadedTokens.delete(key);
          }
        }
      }
    } else if (action.type === 'input') {
      const token = action.valueAfter;
      if (token && typeof token === 'string' && token.length > 5) {
        // Find which fileId this token belongs to, then release it together
        // with any `<fileId>:<i>` siblings from a multi-file auto-upload
        // (input history records only the first token).
        const owner = [..._uploadedTokens.entries()].find(([, t]) => t === token)?.[0];
        if (owner === undefined) continue;
        for (const [key, tok] of [..._uploadedTokens.entries()]) {
          if (key !== owner && !key.startsWith(`${owner}:`)) continue;
          _safeDeleteFile(tok, key);
          _uploadedTokens.delete(key);
        }
      }
    }
  }
}

// ==============================================================================
// Test support
// ==============================================================================

/** Reset every field this module tracks and unbind the configurator accessor.
 *  Only meaningful in tests — production has exactly one AsConfigurator for
 *  the lifetime of the page, so nothing else needs this. Mirrors
 *  History.resetHistory() from undoHistory.ts, but for this module's own
 *  state (baseline snapshots, suppress flag, upload tokens); it does NOT
 *  touch the History stack itself — call History.resetHistory() separately
 *  if a test needs both cleared. */
export function resetConfiguratorHistoryForTests(): void {
  _getConfigurator = () => null;
  _configuratorReady = false;
  _preConfigureSnapshot = null;
  _suppressNextEntry = false;
  _lastConfirmedParams = {};
  _lastCustomData = {};
  if (_customDataChangeTimer) clearTimeout(_customDataChangeTimer);
  _customDataChangeTimer = null;
  _uploadedTokens = new Map();
  _isUndoingConfigure = false;
  _pendingConfigureLabel = null;
  _pendingRestoreCompletion = null;
}

/** Test-only accessors for the internals four browser test files currently
 *  poke directly (they used to reach into AsConfigurator's own private
 *  fields before this module existed — see docs/as-configurator.md). Not
 *  meant for production use. */
export const __test__ = {
  get uploadedTokens() { return _uploadedTokens; },
  get lastConfirmedParams() { return _lastConfirmedParams; },
  set lastConfirmedParams(v: Record<string, any>) { _lastConfirmedParams = v; },
  get lastCustomData() { return _lastCustomData; },
  set lastCustomData(v: Record<string, any>) { _lastCustomData = v; },
  get suppressNextEntry() { return _suppressNextEntry; },
  set configuratorReady(v: boolean) { _configuratorReady = v; },
  isActionDestructive: _isActionDestructive,
  releaseAbandonedTokens: _releaseAbandonedTokens,
};
